// Finora Subscription Configuration
// This file defines all pricing tiers, features, and Stripe IDs

export type SubscriptionTier = 'free' | 'pro' | 'premium';
export type BillingInterval = 'monthly' | 'annual';

// Stripe Price IDs - Updated pricing structure
export const STRIPE_PRICES = {
  pro: {
    monthly: 'price_1Svkf4LfsfNr33fw1uVDTZpx', // €8.99/month
    annual: 'price_1SvkfLLfsfNr33fwM6BgejbK',  // €89/year
  },
  premium: {
    monthly: 'price_1SvkfiLfsfNr33fwTlqLyEkM', // €14.99/month
    annual: 'price_1SvkfxLfsfNr33fw5XHajIDP',  // €149/year
  },
} as const;

// Stripe Product IDs for tier identification
export const STRIPE_PRODUCTS = {
  pro_monthly: 'prod_TtXk4oBWk9GGQx',
  pro_annual: 'prod_TtXk6ZEttzKoA4',
  premium_monthly: 'prod_TtXlWuGzWnDLyG',
  premium_annual: 'prod_TtXlZygbMgqTPA',
  // Legacy products (old pricing)
  legacy_monthly: 'prod_TtP6vOWPMBY3yv',
  legacy_annual: 'prod_TtP7ZuNvjhYK4a',
} as const;

// Map product IDs to tiers
export function getSubscriptionTier(productId: string | null): SubscriptionTier {
  if (!productId) return 'free';
  
  switch (productId) {
    case STRIPE_PRODUCTS.premium_monthly:
    case STRIPE_PRODUCTS.premium_annual:
      return 'premium';
    case STRIPE_PRODUCTS.pro_monthly:
    case STRIPE_PRODUCTS.pro_annual:
    case STRIPE_PRODUCTS.legacy_monthly:
    case STRIPE_PRODUCTS.legacy_annual:
      return 'pro';
    default:
      return 'free';
  }
}

// Pricing display
export const PRICING = {
  free: {
    monthly: 0,
    annual: 0,
    currency: '€',
  },
  pro: {
    monthly: 8.99,
    annual: 89,
    currency: '€',
    monthlyEquivalent: 7.42, // €89/12
    savings: 18.88, // €8.99*12 - €89
  },
  premium: {
    monthly: 14.99,
    annual: 149,
    currency: '€',
    monthlyEquivalent: 12.42, // €149/12
    savings: 30.88, // €14.99*12 - €149
  },
} as const;

// Founding member pricing (first 100 users)
export const FOUNDING_PRICING = {
  pro: {
    annual: 79,
    savings: 10, // vs regular €89
  },
  premium: {
    annual: 129,
    savings: 20, // vs regular €149
  },
} as const;

// Feature definitions by tier
export interface TierFeatures {
  manualTracking: boolean;
  weeklyOverviews: boolean;
  monthlyOverviews: boolean;
  basicReminders: boolean;
  educationTips: boolean;
  connectedAccountsLimit: number;
  bankSync: boolean;
  autoTransactionImport: boolean;
  smartReminders: boolean;
  monthlyInsights: boolean;
  yearlyInsights: boolean;
  savingsTracking: boolean;
  debtTracking: boolean;
  projections12Months: boolean;
  csvExport: boolean;
  multipleBankAccounts: boolean;
  taxDeadlines: boolean;
  taxInsights: boolean;
  multiYearProjections: boolean;
  advancedAutomation: boolean;
  pdfExport: boolean;
  prioritySupport: boolean;
  earlyAccess: boolean;
}

export const TIER_FEATURES: Record<SubscriptionTier, TierFeatures> = {
  free: {
    manualTracking: true,
    weeklyOverviews: true,
    monthlyOverviews: true,
    basicReminders: true,
    educationTips: true,
    connectedAccountsLimit: 1,
    bankSync: false,
    autoTransactionImport: false,
    smartReminders: false,
    monthlyInsights: false,
    yearlyInsights: false,
    savingsTracking: false,
    debtTracking: false,
    projections12Months: false,
    csvExport: false,
    multipleBankAccounts: false,
    taxDeadlines: false,
    taxInsights: false,
    multiYearProjections: false,
    advancedAutomation: false,
    pdfExport: false,
    prioritySupport: false,
    earlyAccess: false,
  },
  pro: {
    manualTracking: true,
    weeklyOverviews: true,
    monthlyOverviews: true,
    basicReminders: true,
    educationTips: true,
    connectedAccountsLimit: 3,
    bankSync: true,
    autoTransactionImport: true,
    smartReminders: true,
    monthlyInsights: true,
    yearlyInsights: true,
    savingsTracking: true,
    debtTracking: true,
    projections12Months: true,
    csvExport: true,
    multipleBankAccounts: false,
    taxDeadlines: false,
    taxInsights: false,
    multiYearProjections: false,
    advancedAutomation: false,
    pdfExport: false,
    prioritySupport: false,
    earlyAccess: false,
  },
  premium: {
    manualTracking: true,
    weeklyOverviews: true,
    monthlyOverviews: true,
    basicReminders: true,
    educationTips: true,
    connectedAccountsLimit: 10,
    bankSync: true,
    autoTransactionImport: true,
    smartReminders: true,
    monthlyInsights: true,
    yearlyInsights: true,
    savingsTracking: true,
    debtTracking: true,
    projections12Months: true,
    csvExport: true,
    multipleBankAccounts: true,
    taxDeadlines: true,
    taxInsights: true,
    multiYearProjections: true,
    advancedAutomation: true,
    pdfExport: true,
    prioritySupport: true,
    earlyAccess: true,
  },
};

// Feature display copy
export const TIER_COPY = {
  free: {
    name: 'Free',
    tagline: 'Get organized',
    headline: 'Build better habits',
    description: 'Start tracking your money and build better financial habits.',
    color: 'muted',
    features: [
      'Manual income & expense tracking',
      'Weekly & monthly overviews',
      'Basic reminders (manual only)',
      'Financial education tips',
      '1 connected account (manual only)',
    ],
    cta: 'Get started',
  },
  pro: {
    name: 'Pro',
    tagline: 'Most Popular',
    headline: 'Automate your finances',
    description: 'For people who want their finances handled automatically.',
    color: 'primary',
    badge: '⭐ Most people choose Pro',
    features: [
      'Everything in Free, plus:',
      'Bank account connection (1–3 accounts)',
      'Automatic transaction import & categorization',
      'Smart reminders (income-based)',
      'Monthly & yearly insights',
      'Savings & debt tracking',
      '12-month outlook',
      'CSV export',
    ],
    cta: 'Start Pro',
  },
  premium: {
    name: 'Premium',
    tagline: 'Complete Financial Clarity',
    headline: 'Plan years ahead',
    description: 'For people who want to stay ahead of their money.',
    color: 'accent',
    badge: '🌿 Complete Control',
    features: [
      'Everything in Pro, plus:',
      'Multiple bank accounts',
      'Country-specific tax deadlines & alerts',
      'Smart tax insights (based on income & region)',
      'Multi-year projections (2–5 years)',
      'Advanced automation suggestions',
      'Accountant-ready PDF exports',
      'Priority support',
      'Early access to new features',
    ],
    cta: 'Go Premium',
  },
} as const;

// Contextual upgrade nudges
export const UPGRADE_NUDGES = {
  missedReminder: {
    tier: 'pro' as SubscriptionTier,
    message: 'Pro helps you stay ahead of payments automatically.',
    context: 'automation',
  },
  viewingFuture: {
    tier: 'premium' as SubscriptionTier,
    message: "See how your finances look next year with Premium.",
    context: 'projections',
  },
  taxTransaction: {
    tier: 'premium' as SubscriptionTier,
    message: 'Premium helps you stay tax-ready, based on where you live.',
    context: 'taxLogic',
  },
  exportAttempt: {
    tier: 'pro' as SubscriptionTier,
    message: 'Export your data anytime with Pro.',
    context: 'export',
  },
  bankSync: {
    tier: 'pro' as SubscriptionTier,
    message: 'Connect your bank and import transactions automatically.',
    context: 'automation',
  },
} as const;

// Discount configurations
export const DISCOUNTS = {
  student: {
    percentOff: 30,
    durationMonths: 12,
    eligibleTiers: ['pro'] as SubscriptionTier[],
    copy: '30% off Pro for 12 months',
  },
  referral: {
    monthsGiven: 1,
    monthsReceived: 1,
    maxMonths: 6,
    qualificationDays: 30,
    copy: 'Give 1 month, get 1 month',
  },
  founding: {
    proAnnual: 79,
    premiumAnnual: 129,
    limit: 100,
    copy: 'Founding Member Price (locked while subscribed)',
  },
} as const;

// Grace period for failed payments (in days)
export const GRACE_PERIOD_DAYS = 7;

// Check if user has access to a feature
export function hasFeatureAccess(
  tier: SubscriptionTier,
  feature: keyof TierFeatures
): boolean {
  return TIER_FEATURES[tier][feature] === true || 
         (typeof TIER_FEATURES[tier][feature] === 'number' && TIER_FEATURES[tier][feature] > 0);
}

// Get minimum tier required for a feature
export function getRequiredTier(feature: keyof TierFeatures): SubscriptionTier {
  if (TIER_FEATURES.free[feature]) return 'free';
  if (TIER_FEATURES.pro[feature]) return 'pro';
  return 'premium';
}

// Check if upgrade is beneficial
export function shouldPromoteUpgrade(
  currentTier: SubscriptionTier,
  targetTier: SubscriptionTier
): boolean {
  const tierOrder: SubscriptionTier[] = ['free', 'pro', 'premium'];
  return tierOrder.indexOf(targetTier) > tierOrder.indexOf(currentTier);
}
